import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { formatDate, isValid, parse } from 'date-fns';
import differenceInYears from 'date-fns/differenceInYears';
import { PlusIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import { BackOfficePermissionsRedirectWrapper } from '../../../components/pages/backoffice/permissions-redirect-wrapper.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { DateInputWithDatePicker } from '../../../components/ui/date-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { DISTRICTS } from '../../../utils/constants.js';
import { formatDateToISO } from '../../../utils/format.js';
import { REGEX_DATE_EU } from '../../../utils/regex.js';

export function CustomersCreate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useSnapshot(authState);

  const formCreateCustomer = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      cc: '',
      email: '',
      birthDate: '',
      phone: '',
      location: '',
      address: '',
      district: '',
      zipCode: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Apelido tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        cc: yup
          .string()
          .min(6, 'Nº Identificação tem de ter pelo menos 6 caracteres')
          .required('Nº Identificação é um campo obrigatório'),
        birthDate: yup
          .string()
          .required('Data de Nascimento é um campo obrigatório')
          .matches(
            REGEX_DATE_EU,
            'Data de Nascimento possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'dob',
            'Apenas clientes com 18 anos ou mais podem participar na campanha',
            function (value) {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());

              return differenceInYears(new Date(), parsedDate) >= 18;
            },
          ),
        phone: yup.string().required('Telemóvel é um campo obrigatório'),
        /*
          .matches(
            /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/,
            'Nº de Telemóvel inválido',
          ),
        */
        location: yup.string().required('Localidade é um campo obrigatório'),
      }),
    ),
  });

  const mutationCreateCustomerConsentRequest = useMutation({
    mutationFn: async (data) => {
      try {
        const { data: createCustomerConsentReq } = await msCatalystApi.post(
          `/customers/consent-requests`,
          {
            customer: JSON.stringify(data),
            searchCustomerIdentificationDoc: data.cc,
            searchCustomerEmail: data.email,
            staff: `${user.firstName} ${user.lastName}`,
            action: 'create',
            currentCustomerIdentificationDoc: data.cc,
            currentCustomerEmail: data.email,
          },
        );

        return createCustomerConsentReq.data;
      } catch (error) {
        throw error;
      }
    },
  });

  const mutationCheckForDuplicateEmailCustomer = useMutation({
    mutationFn: async (data) => {
      const { data: checkForDuplicateEmailCustomer } = await msCatalystApi.get(
        `customers?email=${data.email}`,
      );
      return checkForDuplicateEmailCustomer;
    },
  });

  const mutationCheckForDuplicateCcCustomer = useMutation({
    mutationFn: async (data) => {
      const { data: checkForDuplicateCcCustomer } = await msCatalystApi.get(
        `customers?cc=${data.cc}`,
      );
      return checkForDuplicateCcCustomer;
    },
  });

  useEffect(() => {
    searchParams.forEach((value, key) => {
      formCreateCustomer.setValue(key, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, []);

  const onFormSubmit = async (data) => {
    try {
      if (data.email !== '') {
        const { data: checkForDuplicateEmailCustomer } =
          await mutationCheckForDuplicateEmailCustomer.mutateAsync({
            email: data.email,
          });

        if (checkForDuplicateEmailCustomer?.length) {
          return toast.error('Erro ao criar conta cliente', {
            description: 'Já existe um Cliente com o Email inserido',
          });
        }
      }

      const { data: checkForDuplicateCcCustomer } =
        await mutationCheckForDuplicateCcCustomer.mutateAsync({ cc: data.cc });

      if (checkForDuplicateCcCustomer?.length) {
        return toast.error('Erro ao criar conta cliente', {
          description:
            'Já existe um Cliente com o Número de Identificação inserido',
        });
      }

      const { data: searchConsentRequestRes } = await msCatalystApi.get(
        `customers/consent-requests/search/${data.cc}`,
      );

      if (searchConsentRequestRes?.data) {
        await msCatalystApi.delete(
          `/customers/consent-requests/${searchConsentRequestRes.data.ROWID}`,
        );
      }

      await mutationCreateCustomerConsentRequest.mutateAsync({
        ...data,
        birthDate: formatDateToISO(data.birthDate),
      });

      toast.success('Conta pendente de aprovação de consentimento');

      navigate(`/backoffice/customers`);
    } catch (err) {
      toast.error('Erro ao criar conta cliente', {
        description:
          err.message === 'Request failed with status code 409'
            ? 'Pedido de consentimento com nº de identificação já foi criado'
            : 'Por favor tente novamente mais tarde',
      });
    }
  };

  return (
    <BackOfficePermissionsRedirectWrapper>
      <FormUnsavedConfirmationWrapper form={formCreateCustomer}>
        <Heading className="pb-6">Criar Cliente</Heading>

        <Form {...formCreateCustomer}>
          <form onSubmit={formCreateCustomer.handleSubmit(onFormSubmit)}>
            <div className="flex flex-col space-y-10">
              <FormCard>
                <FormField
                  control={formCreateCustomer.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCustomer.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Apelido</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCustomer.control}
                  name="cc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nº Identificação</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCustomer.control}
                  name="birthDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATE_EU)) {
                      const [day, month, year] = field.value.split('-');
                      selectedDate = new Date(`${year}-${month}-${day}`);
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true}>
                          Data de Nascimento
                        </FormLabel>
                        <FormControl>
                          <DateInputWithDatePicker
                            input={{
                              ...field,
                              onChange: (e) => {
                                const date = e.target.value;

                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(date);
                              },
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (date) => {
                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(
                                  formatDate(new Date(date), 'dd-MM-yyyy'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Nascimento tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formCreateCustomer.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Telemóvel</FormLabel>
                      <FormControl>
                        <Input type="tel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCustomer.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>

              <div className="flex flex-col gap-5">
                <h2 className="text-xl font-medium">Localização</h2>

                <FormCard>
                  <FormField
                    control={formCreateCustomer.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required={true}>Localidade</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formCreateCustomer.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Morada</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formCreateCustomer.control}
                    name="district"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Distrito</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {DISTRICTS.map((item, i) => {
                              return (
                                <SelectItem key={i} value={item}>
                                  {item}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formCreateCustomer.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Código Postal</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormCard>
              </div>
            </div>

            <FormFooter>
              <CancelButton />

              <Button
                type="submit"
                disabled={
                  !formCreateCustomer.formState.isDirty ||
                  formCreateCustomer.formState.isSubmitting
                }
                isLoading={formCreateCustomer.formState.isSubmitting}
                loadingMessage="A criar..."
              >
                <PlusIcon />
                Criar
              </Button>
            </FormFooter>
          </form>
        </Form>
      </FormUnsavedConfirmationWrapper>
    </BackOfficePermissionsRedirectWrapper>
  );
}
